import React, { useState, useEffect, useMemo } from "react";
import { baseUrl } from "../../../../../../../../../../network/constants/constants";
import { getConfig } from "../../../../../../../../../../network/auth";
import axios from "../../../../../../../../../../network/api";
import BookingAddTable from "./bookingAddTable/bookingAddTable";
import { Spinner } from "react-bootstrap";
import "./bookingAdd.css";
import BookingAddEditForm from "./bookingAddEditForm/bookingAddEditForm";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { Snackbar, Alert } from "@mui/material";
import RealTimeBookingDataGrid from "../bookingDetails/bookingDataGrid/RealTimeBookingDataGrid";
import AppConstants from "../../../../../../../../../AppConstants";
import DateFilter from "../bookingDetails/dateFilter/dateFilter";

const JWT_TOKEN_KEY = AppConstants.JWT;
const WS_BASE_URL = "wss://main.hifiservices.in/ws/dashboard/bookings/";

const BookingAdd = ({ franchiseId, setEditing }) => {
  const [loading, setLoading] = useState(false);
  const [bookingList, setBookingList] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [serviceList, setServiceList] = useState([]);

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);

  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [workerList, setWorkerList] = useState([]);
  const [selectedService, setSelectedService] = useState("");

  const [realTimeBookingsCount, setRealTimeBookingsCount] = useState(0);
  const [isTokenAvailable, setIsTokenAvailable] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddBooking = () => {
    setIsEdit(false);
    setSelectedBooking(null);
    setSelectedService("");
    setOpenForm(true);
  };

  const handleEditBooking = (params) => {
    setIsEdit(true);
    setEditing(true);
    setSelectedBooking(params.row);
    setSelectedService(params.row.service_id || "");
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditing(false);
    setSelectedBooking(null);
    setSelectedService("");
  };

  const handleDeleteClick = (service) => {
    setBookingToDelete(service.id);
    setShowDeletePopup(true);
  };

  const confirmDelete = () => {
    if (bookingToDelete) {
      deleteService(bookingToDelete);
      setBookingToDelete(null);
      setShowDeletePopup(false);
    }
  };

  const handleDeleteClose = () => {
    setShowDeletePopup(false);
  };

  const deleteService = (bookingId) => {
    setLoading(true);

    axios
      .delete(`${baseUrl}superadmin/booking-delete/${bookingId}/`, config)
      .then((response) => {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        fetchBookingLists();
      })
      .catch((error) => {
        console.error("Error deleting service:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete booking",
          severity: "error",
        });
        setLoading(false);
      });
  };

  const config = useMemo(() => {
    return getConfig();
  }, []);

  const fetchBookingLists = (start_date = null, end_date = null) => {
    setLoading(true);
    axios
      .get(baseUrl + `superadmin/booking-list-by-business-id/${franchiseId}`, {
        params: {
          start_date: start_date || "",
          end_date: end_date || "",
        },
        ...config,
      })
      .then((response) => {
        setBookingList(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Category Error:", error);
        setLoading(false);
      });
  };

  const fetchService = () => {
    setLoading(true);
    axios
      .get(baseUrl + `app/services-list-by-business/${franchiseId}/`, config)
      .then((response) => {
        setLoading(false);
        setServiceList(response.data);
      })
      .catch((error) => {
        console.error("Category Error:", error);
        setLoading(false);
      });
  };

  const fetchWorker = () => {
    setLoading(true);
    axios
      .get(baseUrl + `superadmin/workers-by-business/${franchiseId}/`, config)
      .then((response) => {
        setLoading(false);
        setWorkerList(response.data);
      })
      .catch((error) => {
        console.error("Worker Error:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    const token = localStorage.getItem(JWT_TOKEN_KEY);
    setIsTokenAvailable(!!token);

    fetchBookingLists();
    fetchService();
    fetchWorker();
  }, []);

  const handleRefresh = () => {
    fetchBookingLists();
    fetchService();
    fetchWorker();
  };

  const handleBookingSubmit = (formData) => {
    const url = selectedBooking
      ? `${baseUrl}superadmin/booking-edit/${selectedBooking.id}/`
      : `${baseUrl}superadmin/add-booking/`;

    const method = selectedBooking ? "patch" : "post";

    const payload = {
      service_id: formData.serviceName,
      worker: formData.worker,
      name: formData.customerName,
      phone: formData.phone,
      address: formData.address,
      booked_date: formData.bookingDate,
      booked_time: formData.bookingTime,
      latitude: latitude,
      longitude: longitude,
      service_charge: formData.serviceCharge ?? "",
      miscellaneous_price: formData.miscellaneousPrice ?? "",
      total_price: formData.totalPrice ?? "",
      ...(formData.status ? { status: formData.status } : {}),
      ...(formData.status === "Cancelled" ? { refund: !!formData.refund } : {}), 
    };

    axios({
      method,
      url,
      data: payload,
      headers: {
        ...config.headers,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        fetchBookingLists();
        setOpenForm(false);
        setSelectedService("");
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred. Please try again.";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
        console.error("Error saving service booking:", error);
      });
  };

  const handleFilterBookings = () => {
    if (!startDate || !endDate) {
      setSnackbarMessage("Please select both start and end dates.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    fetchBookingLists(startDate, endDate);
    setSnackbarMessage("Filters applied successfully.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    fetchBookingLists();
    setSnackbarMessage("Filter reset. Showing all data.");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  return (
    <div className="mt-3 booking-add-div">
      {isTokenAvailable ? (
        <RealTimeBookingDataGrid
          franchiseId={franchiseId}
          websocketUrl={WS_BASE_URL}
          setRealTimeBookingsCount={setRealTimeBookingsCount}
          tokenKey={JWT_TOKEN_KEY}
        />
      ) : (
        <div
          style={{
            textAlign: "center",
            color: "#d32f2f",
            padding: "20px",
            fontWeight: "bold",
            backgroundColor: "#fff3e0",
            borderRadius: "8px",
            margin: "10px 0",
            border: "1px solid #ffb74d",
          }}
        >
          ⚠️ Authentication token not found. Real-time feed disabled.
        </div>
      )}

      <div className="text-end">
        <button
          className="btn-refresh-service-man mx-2"
          onClick={handleRefresh}
        >
          ⟳ Refresh
        </button>
        <button className="btn-add-booking" onClick={handleAddBooking}>
          + Add Booking
        </button>
      </div>
      <DateFilter
        bookingList={bookingList}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarSeverity={setSnackbarSeverity}
        handleFilterBookings={handleFilterBookings}
        handleReset={handleReset}
      />
      {loading ? (
        <div className="loader-container">
          <Spinner className="spinner-loader" />
        </div>
      ) : (
        <BookingAddTable
          bookingList={bookingList}
          onEdit={handleEditBooking}
          onDelete={handleDeleteClick}
        />
      )}

      <BookingAddEditForm
        open={openForm}
        onClose={handleCloseForm}
        booking={isEdit ? selectedBooking : null}
        serviceList={serviceList}
        onSubmit={handleBookingSubmit}
        setLatitude={setLatitude}
        setLongitude={setLongitude}
        latitude={latitude}
        longitude={longitude}
        setLoading={setLoading}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        workerList={workerList}
        setWorkerList={setWorkerList}
      />

      <Dialog
        open={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        keepMounted
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Do you really want to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="small"
            color="error"
            style={{ textTransform: "none" }}
            onClick={confirmDelete}
          >
            Delete
          </Button>
          <Button
            size="small"
            style={{
              textTransform: "none",
              color: "grey",
              outline: "1px solid grey",
            }}
            onClick={handleDeleteClose}
          >
            cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BookingAdd;